<section class="flex h-full w-full flex-col overflow-hidden bg-white text-slate-900" role="dialog">
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-4 py-3">
    @if (formOpen && cabinets.length) {
      <app-button btnType="icon" [ariaLabel]="'back' | translate" (onChangeClick)="closeForm()">
        <app-icon>arrow_back</app-icon>
      </app-button>
    } @else {
      <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-red-50 text-red-600">
        <app-icon>local_shipping</app-icon>
      </div>
    }
    <div class="flex min-w-0 flex-1 flex-col">
      <h2 class="min-w-0 truncate !m-0 !text-base !font-bold !leading-6">
        @if (formOpen) {
          {{ (editing ? 'novaposhta.settings.edit_title' : 'novaposhta.settings.add_title') | translate }}
        } @else {
          {{ 'novaposhta.title' | translate }}
        }
      </h2>
      <span class="truncate text-xs text-slate-400">{{ 'novaposhta.settings.subtitle' | translate }}</span>
    </div>
    <app-button btnType="icon" [ariaLabel]="'close' | translate" (onChangeClick)="done()">
      <app-icon>close</app-icon>
    </app-button>
  </header>

  @if (loading) {
    <div class="flex flex-1 flex-col gap-3 px-4 py-4">
      <div class="h-24 animate-pulse rounded-lg bg-slate-100"></div>
      <div class="h-24 animate-pulse rounded-lg bg-slate-100"></div>
    </div>
  } @else if (formOpen) {
    <div class="crm-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
      @if (!cabinets.length) {
        <div class="flex gap-3 rounded-lg border !border-red-100 bg-red-50/60 p-3">
          <app-icon class="shrink-0 text-red-500">waving_hand</app-icon>
          <div class="flex flex-col gap-1">
            <span class="text-sm font-semibold text-slate-900">{{ 'novaposhta.settings.welcome_title' | translate }}</span>
            <span class="text-sm leading-5 text-slate-600">{{ 'novaposhta.settings.welcome_text' | translate }}</span>
          </div>
        </div>
      }

      <app-input
        [formControl]="form.controls.name"
        [label]="'novaposhta.settings.name' | translate"
        [required]="true"
        [maxlength]="nameMaxLength"
        [placeholder]="'novaposhta.settings.name_placeholder' | translate"
      />

      <div class="flex flex-col gap-1">
        <app-input
          inputType="password"
          [toggleMask]="true"
          [formControl]="form.controls.api_key"
          [label]="'novaposhta.settings.api_key' | translate"
          [required]="!editing"
          [placeholder]="
            editing
              ? ('novaposhta.settings.api_key_keep' | translate: { last4: editing.api_key_last4 })
              : ('novaposhta.settings.api_key_placeholder' | translate)
          "
        />
        <p class="!m-0 text-xs leading-5 text-slate-400">{{ 'novaposhta.settings.api_key_hint' | translate }}</p>
      </div>

      <div class="flex flex-col gap-3 rounded-lg border !border-slate-200 p-3">
        <div class="flex flex-col">
          <span class="text-sm font-medium text-slate-700">{{ 'novaposhta.settings.send_from' | translate }}</span>
          <span class="text-xs text-slate-400">{{ 'novaposhta.settings.send_from_hint' | translate }}</span>
        </div>
        <app-select
          [formControl]="form.controls.sender_city_ref"
          [options]="cityOptions"
          optionLabel="label"
          optionValue="ref"
          [filter]="true"
          [loading]="citiesLoading"
          [required]="true"
          [label]="'novaposhta.form.city' | translate"
          [placeholder]="'novaposhta.form.city_placeholder' | translate"
          [emptyMessage]="'novaposhta.form.city_search_hint' | translate"
          [emptyFilterMessage]="'novaposhta.form.nothing_found' | translate"
          (filterChange)="onCitySearch($event)"
          (valueChange)="onCityPicked($event)"
        />
        <app-select
          [formControl]="form.controls.sender_warehouse_ref"
          [options]="warehouseOptions"
          optionLabel="name"
          optionValue="ref"
          [filter]="true"
          [loading]="warehousesLoading"
          [required]="true"
          [label]="'novaposhta.form.sender_warehouse' | translate"
          [placeholder]="(form.controls.sender_city_ref.value ? 'novaposhta.form.warehouse_placeholder' : 'novaposhta.form.pick_city_first') | translate"
          [emptyFilterMessage]="'novaposhta.form.nothing_found' | translate"
          (filterChange)="onWarehouseSearch($event)"
          (valueChange)="onWarehousePicked($event)"
        />
      </div>

      <div class="flex items-center justify-between gap-3 rounded-lg border !border-slate-200 px-3 py-2.5">
        <div class="flex flex-col">
          <span class="text-sm font-medium text-slate-700">{{ 'novaposhta.settings.is_default' | translate }}</span>
          <span class="text-xs text-slate-400">{{ 'novaposhta.settings.is_default_hint' | translate }}</span>
        </div>
        <app-toggle [formControl]="form.controls.is_default">
          <span class="sr-only">{{ 'novaposhta.settings.is_default' | translate }}</span>
        </app-toggle>
      </div>

      <div class="flex gap-2 rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-500">
        <app-icon class="!h-4 !w-4 shrink-0 !text-[16px] text-slate-400">verified_user</app-icon>
        <span>{{ 'novaposhta.settings.key_check_info' | translate }}</span>
      </div>
    </div>

    <footer class="flex shrink-0 gap-2 border-t !border-slate-100 px-4 py-3">
      @if (cabinets.length) {
        <app-button btnType="stroked" class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" (onChangeClick)="closeForm()">
          {{ 'cancel' | translate }}
        </app-button>
      }
      <app-button class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" [disabled]="saving" (onChangeClick)="save()">
        @if (saving) {
          <span class="flex items-center justify-center gap-2">
            <app-icon class="!h-4 !w-4 animate-spin !text-[16px] !leading-none">progress_activity</app-icon>
            {{ 'novaposhta.settings.checking_key' | translate }}
          </span>
        } @else {
          {{ (editing ? 'save' : 'novaposhta.settings.connect') | translate }}
        }
      </app-button>
    </footer>
  } @else {
    <div class="crm-scroll flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-slate-50 px-4 py-4">
      @for (cabinet of cabinets; track cabinet.id) {
        <article
          class="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm transition"
          [ngClass]="cabinet.is_default ? '!border-orange-200' : '!border-slate-200'"
          [class.opacity-60]="busyId === cabinet.id"
        >
          <div class="flex items-start gap-3">
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="truncate text-sm font-semibold text-slate-900">{{ cabinet.name }}</span>
                @if (cabinet.is_default) {
                  <span class="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                    {{ 'novaposhta.settings.default_badge' | translate }}
                  </span>
                }
                @if (cabinet.status === 'invalid_key') {
                  <span class="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                    {{ 'novaposhta.settings.invalid_key_badge' | translate }}
                  </span>
                } @else {
                  <span class="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                    {{ 'novaposhta.settings.active_badge' | translate }}
                  </span>
                }
              </div>
              <span class="font-mono text-xs text-slate-400">API •••• {{ cabinet.api_key_last4 }}</span>
            </div>
            <div class="flex shrink-0 items-center">
              @if (!cabinet.is_default) {
                <app-button
                  btnType="icon"
                  [ariaLabel]="'novaposhta.settings.make_default' | translate"
                  [matTooltip]="'novaposhta.settings.make_default' | translate"
                  [disabled]="busyId === cabinet.id"
                  (onChangeClick)="makeDefault(cabinet)"
                >
                  <app-icon class="text-slate-400">star</app-icon>
                </app-button>
              }
              <app-button
                btnType="icon"
                [ariaLabel]="'edit' | translate"
                [matTooltip]="'edit' | translate"
                [disabled]="busyId === cabinet.id"
                (onChangeClick)="openForm(cabinet)"
              >
                <app-icon class="text-slate-500">edit</app-icon>
              </app-button>
              <app-button
                btnType="icon"
                [ariaLabel]="'delete' | translate"
                [matTooltip]="'delete' | translate"
                [disabled]="busyId === cabinet.id"
                (onChangeClick)="remove(cabinet)"
              >
                <app-icon class="text-rose-500">delete_outline</app-icon>
              </app-button>
            </div>
          </div>

          @if (cabinet.sender; as sender) {
            <dl class="!m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 border-t !border-slate-100 pt-3 text-sm">
              <dt class="text-slate-400">{{ 'novaposhta.settings.sender' | translate }}</dt>
              <dd class="!m-0 min-w-0 truncate font-medium text-slate-800">{{ sender.name }}</dd>
              <dt class="text-slate-400">{{ 'novaposhta.settings.contact' | translate }}</dt>
              <dd class="!m-0 min-w-0 truncate text-slate-700">{{ sender.contact_name }} · {{ formatNpPhone(sender.phone) }}</dd>
              <dt class="text-slate-400">{{ 'novaposhta.settings.send_from' | translate }}</dt>
              @if (sender.city) {
                <dd class="!m-0 min-w-0 truncate text-slate-700">
                  {{ sender.city.name }}@if (sender.warehouse) {, {{ sender.warehouse.name }}}
                </dd>
              } @else {
                <dd class="!m-0 min-w-0 truncate font-medium text-amber-700">{{ 'novaposhta.settings.send_from_missing' | translate }}</dd>
              }
            </dl>
            @if (sender.type === 'private_person') {
              <div class="flex gap-2 rounded-lg border !border-sky-100 bg-sky-50 px-3 py-2.5 text-xs leading-5 text-sky-800">
                <app-icon class="!h-4 !w-4 shrink-0 !text-[16px] text-sky-500">info</app-icon>
                <span>{{ 'novaposhta.private_person_limits' | translate }}</span>
              </div>
            }
          } @else {
            <p class="!m-0 border-t !border-slate-100 pt-3 text-sm font-medium text-amber-700">
              {{ 'novaposhta.settings.send_from_missing' | translate }}
            </p>
          }
        </article>
      } @empty {
        <div class="flex flex-col items-center gap-3 rounded-lg border border-dashed !border-slate-300 bg-white px-6 py-10 text-center">
          <div class="grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-500">
            <app-icon>key</app-icon>
          </div>
          <span class="text-sm font-semibold text-slate-900">{{ 'novaposhta.settings.empty_title' | translate }}</span>
          <span class="max-w-xs text-sm text-slate-500">{{ 'novaposhta.settings.empty_text' | translate }}</span>
        </div>
      }
    </div>

    <footer class="flex shrink-0 gap-2 border-t !border-slate-100 px-4 py-3">
      <app-button btnType="stroked" class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" (onChangeClick)="openForm(null)">
        <span class="flex items-center justify-center gap-1">
          <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
          {{ 'novaposhta.settings.add' | translate }}
        </span>
      </app-button>
      <app-button class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" (onChangeClick)="done()">
        {{ 'novaposhta.settings.done' | translate }}
      </app-button>
    </footer>
  }
</section>
